import { CustomFieldInputs, CustomFieldsView, useCustomColumns, useCustomFields } from '../components/CustomFields.tsx';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { standardRateNow } from '../lib/taxes.ts';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { qtyText, UNITS, type Unit } from './Invoices.tsx';
import { parseQty } from './manage/Stock.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

type State = 'planning' | 'active' | 'on_hold' | 'done' | 'cancelled';
type CertState = 'draft' | 'approved' | 'invoiced' | 'cancelled';
type CostKind = 'material' | 'labour' | 'subcontract' | 'equipment' | 'other';

interface Row {
  id: string;
  code: string;
  name: string;
  state: State;
  customer_name: string;
  manager_name: string | null;
  end_date: string | null;
  contract: number;
  certified: number;
  progress: number | null;
  cost: number;
  budget: number;
  margin: number;
  over_budget: boolean;
}
interface Project {
  id: string;
  code: string;
  name: string;
  customer: string;
  customer_name: string;
  site: string | null;
  manager: string | null;
  manager_name: string | null;
  start_date: string | null;
  end_date: string | null;
  state: State;
  tax_rate: number;
  retention_pct: number;
  retention_released_on: string | null;
  location: string | null;
  location_name: string | null;
  note: string | null;
}
interface Item {
  id: string;
  code: string | null;
  name: string;
  unit: Unit;
  qty: number;
  unit_price: number;
  budget_unit_cost: number;
  variation: boolean;
  value: number;
  budget: number;
  certified: number;
  progress: number;
  cost: number;
  over_budget: boolean;
}
interface CertRow {
  id: string;
  number: number;
  period_from: string | null;
  period_to: string;
  state: CertState;
  net: number;
  total: number;
  retention: number;
  invoice: string | null;
  invoice_name: string | null;
  invoice_state: string | null;
  invoice_open: number;
}
interface CostRow {
  id: string;
  date: string;
  kind: CostKind;
  description: string;
  qty: number | null;
  amount: number;
  source: string | null;
  item: string | null;
}
interface Figures {
  contract: number;
  original: number;
  variations: number;
  budget: number;
  certified: number;
  invoiced: number;
  collected: number;
  retention: number;
  cost: number;
  costs: Record<CostKind, number>;
  progress: number | null;
  spent: number | null;
  margin: number;
  expected_margin: number;
  retention_due: number;
  retention_held: number;
}
interface Detail {
  today: string;
  project: Project;
  items: Item[];
  certificates: CertRow[];
  costs: CostRow[];
  figures: Figures;
  has_purchase: boolean;
}

const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  planning: { tone: 'neutral', icon: 'note' },
  active: { tone: 'ok', icon: 'circle' },
  on_hold: { tone: 'warn', icon: 'pause' },
  done: { tone: 'info', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const CERT: Record<CertState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  approved: { tone: 'info', icon: 'check' },
  invoiced: { tone: 'ok', icon: 'invoice' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const KINDS: CostKind[] = ['material', 'labour', 'subcontract', 'equipment', 'other'];
const KIND_ICON: Record<CostKind, IconName> = { material: 'box', labour: 'users', subcontract: 'transfer', equipment: 'truck', other: 'receipt' };
const NEXT: Record<State, State[]> = { planning: ['active', 'cancelled'], active: ['on_hold', 'done'], on_hold: ['active', 'done', 'cancelled'], done: ['active'], cancelled: ['planning'] };

const unit = (u: Unit) => t(`inv.u_${u}` as MessageKey);
const StateBadge = ({ s }: { s: State }) => <StatusBadge tone={STATE[s].tone} icon={STATE[s].icon} label={t(`prj.st_${s}`)} />;
const CertBadge = ({ s }: { s: CertState }) => <StatusBadge tone={CERT[s].tone} icon={CERT[s].icon} label={t(`prj.cs_${s}`)} />;

function Bar({ pct, warn, label }: { pct: number; warn?: boolean; label: string }) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <span className={`prj-bar${warn ? ' warn' : ''}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <span style={{ width: `${w}%` }} />
    </span>
  );
}
const pctText = (p: number | null) => (p === null ? '—' : `${p} %`);
const lek = (minor: number) => (minor ? String(minor / 100) : '');

/* ---------------------------------------------------------------- List */

type Filter = 'live' | 'all' | 'done';

export function Projects() {
  const { can } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = (['live', 'all', 'done'].includes(params.get('show') ?? '') ? params.get('show') : 'live') as Filter;
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const { data } = useAction<{ projects: Row[]; totals: { active: number; contract: number; to_certify: number; retention: number; over_budget: number } }>('construction.projects', { search: search.trim() || undefined });
  const rows = (data?.projects ?? []).filter((p) => (filter === 'all' ? true : filter === 'done' ? p.state === 'done' || p.state === 'cancelled' : p.state !== 'done' && p.state !== 'cancelled'));
  const extra = useCustomColumns('construction.project', rows.map((p) => p.id));
  return (
    <Shell>
      <div className="page reports prj">
        <div className="page-head">
          <div>
            <h1>{t('prj.title')}</h1>
            <p className="muted">{t('prj.subtitle')}</p>
          </div>
          {can('construction.manage') ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              {t('prj.new')}
            </Button>
          ) : null}
        </div>
        {!data ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : (
          <div className="rep-grid">
            <section className="rep-kpis" aria-label={t('prj.totals')}>
              <Kpi label={t('prj.k_active')} value={String(data.totals.active)} hint={t('prj.k_active_hint', { value: money(data.totals.contract) })} />
              <Kpi label={t('prj.k_to_certify')} value={money(data.totals.to_certify)} hint={t('prj.k_to_certify_hint')} />
              <Kpi label={t('prj.k_retention')} value={money(data.totals.retention)} hint={t('prj.k_retention_hint')} />
              <Kpi label={t('prj.k_over')} value={String(data.totals.over_budget)} warn={data.totals.over_budget > 0} hint={t('prj.k_over_hint')} />
            </section>
            <div className="toolbar prj-bar-row">
              <div className="segmented" role="radiogroup" aria-label={t('prj.show')}>
                {(['live', 'done', 'all'] as const).map((f) => (
                  <button key={f} type="button" role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setParams(f === 'live' ? {} : { show: f }, { replace: true })}>
                    {t(`prj.show_${f}`)}
                  </button>
                ))}
              </div>
              <input className="input prj-search" type="search" placeholder={t('prj.search')} aria-label={t('prj.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <section className="panel rep-wide" aria-label={t('prj.title')}>
              {!rows.length && !search ? (
                <EmptyState icon="layers" title={t('prj.empty')} body={t('prj.empty_body')} />
              ) : (
                <Table
                  view="projects.list"
                  head={[t('prj.c_project'), t('prj.c_customer'), t('prj.c_progress'), t('prj.c_contract'), t('prj.c_cost'), t('prj.c_margin'), ...extra.columns.map((f) => f.label), t('prj.c_state')]}
                  left={[1, ...extra.columns.map((_, i) => 6 + i)]}
                  phoneHide={[1, 2, 3, 4, ...extra.columns.map((_, i) => 6 + i)]}
                  empty={t('prj.none_found')}
                  rows={rows.map((p) => ({
                    key: p.id,
                    onClick: () => navigate(`/projects/${p.id}`),
                    cells: [
                      <span className="prj-name">
                        <span className="strong">{p.name}</span>
                        <span className="muted small">{[p.code, p.manager_name].filter(Boolean).join(' · ')}</span>
                      </span>,
                      p.customer_name,
                      <span className="prj-prog">
                        <Bar pct={p.progress ?? 0} label={t('prj.c_progress')} />
                        <span className="tabular small">{pctText(p.progress)}</span>
                      </span>,
                      <span className="tabular">{money(p.contract)}</span>,
                      <span className={`tabular${p.over_budget ? ' text-danger' : ''}`}>{money(p.cost)}</span>,
                      <span className={`tabular strong${p.margin < 0 ? ' text-danger' : ''}`}>{money(p.margin)}</span>,
                      ...extra.columns.map((f) => extra.cell(f, p.id)),
                      <StateBadge s={p.state} />,
                    ],
                  }))}
                />
              )}
            </section>
          </div>
        )}
      </div>
      {creating ? <ProjectForm onClose={() => setCreating(false)} onSaved={(id) => navigate(`/projects/${id}`)} /> : null}
    </Shell>
  );
}

function ProjectForm({ project, onClose, onSaved }: { project?: Project; onClose: () => void; onSaved: (id: string) => void }) {
  const { data: customers } = useAction<{ id: string; name: string; active: boolean; is_company: boolean }[]>('customers.list', {});
  const { data: people } = useAction<{ id: string; name: string }[]>('construction.people', {});
  const { data: places } = useAction<{ id: string; name: string; active: boolean }[]>('company.locations', {});
  const { me } = useSession();
  const [f, setF] = useState({
    name: project?.name ?? '',
    customer: project?.customer ?? '',
    site: project?.site ?? '',
    manager: project?.manager ?? me?.userId ?? '',
    start_date: project?.start_date ?? todayIso(),
    end_date: project?.end_date ?? '',
    tax_rate: project?.tax_rate ?? standardRateNow(),
    retention: project ? String(project.retention_pct) : '5',
    location: project?.location ?? '',
    note: project?.note ?? '',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const { busy, run } = useSave();
  const cf = useCustomFields('construction.project', project?.id);
  const [tried, setTried] = useState(false);
  const ret = Number(f.retention.replace(',', '.'));
  const retOk = f.retention.trim() !== '' && Number.isFinite(ret) && ret >= 0 && ret <= 20;
  const ok = f.name.trim().length >= 2 && f.customer && retOk && (!f.end_date || !f.start_date || f.end_date >= f.start_date);
  const save = async () => {
    setTried(true);
    if (!cf.valid) return;
    let id = project?.id ?? '';
    const done = await run(async () => {
      const r = await action<{ id: string }>('construction.save_project', {
        project: project?.id,
        name: f.name.trim(),
        customer: f.customer,
        site: f.site,
        manager: f.manager || null,
        start_date: f.start_date || null,
        end_date: f.end_date || null,
        tax_rate: f.tax_rate,
        retention_pct: ret,
        location: f.location || null,
        note: f.note,
      });
      id = r.id;
      await cf.save(id);
    }, t('prj.saved'));
    if (done) onSaved(id);
  };
  return (
    <Dialog
      title={project ? t('prj.edit') : t('prj.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('prj.f_name')} hint={t('prj.f_name_hint')} required>
          <input className="input" value={f.name} maxLength={120} onChange={(e) => set('name', e.target.value)} data-autofocus />
        </Field>
        <div className="dn-fields">
          <Field label={t('prj.f_customer')} hint={t('prj.f_customer_hint')} required>
            <select className="input" value={f.customer} onChange={(e) => set('customer', e.target.value)}>
              <option value="">{t('prj.choose')}</option>
              {(customers ?? [])
                .filter((c) => c.active !== false || c.id === f.customer)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label={t('prj.f_manager')}>
            <select className="input" value={f.manager} onChange={(e) => set('manager', e.target.value)}>
              <option value="">{t('prj.nobody')}</option>
              {(people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('prj.f_start')}>
            <input className="input" type="date" value={f.start_date} onChange={(e) => set('start_date', e.target.value)} />
          </Field>
          <Field label={t('prj.f_end')} error={f.end_date && f.start_date && f.end_date < f.start_date ? t('prj.f_end_bad') : null}>
            <input className="input" type="date" value={f.end_date} onChange={(e) => set('end_date', e.target.value)} />
          </Field>
          <Field label={t('prj.f_vat')}>
            <TaxRateSelect value={f.tax_rate} onChange={(r) => set('tax_rate', r)} />
          </Field>
          <Field label={t('prj.f_retention')} hint={t('prj.f_retention_hint')} error={retOk ? null : t('prj.f_retention_bad')}>
            <input className="input tabular" inputMode="decimal" value={f.retention} onChange={(e) => set('retention', e.target.value)} />
          </Field>
          <Field label={t('prj.f_site')}>
            <input className="input" value={f.site} maxLength={200} onChange={(e) => set('site', e.target.value)} />
          </Field>
          <Field label={t('prj.f_store')} hint={t('prj.f_store_hint')}>
            <select className="input" value={f.location} onChange={(e) => set('location', e.target.value)}>
              <option value="">{t('prj.main_store')}</option>
              {(places ?? [])
                .filter((l) => l.active !== false || l.id === f.location)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
        <Field label={t('prj.f_note')}>
          <textarea className="input" rows={3} maxLength={2000} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
        <CustomFieldInputs cf={cf} tried={tried} />
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- One project */

type Tab = 'overview' | 'boq' | 'certificates' | 'costs' | 'diary';
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'overview', icon: 'chart' },
  { key: 'boq', icon: 'list' },
  { key: 'certificates', icon: 'invoice' },
  { key: 'costs', icon: 'cash' },
  { key: 'diary', icon: 'book' },
];

export function ProjectPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const { data, reload } = useAction<Detail>('construction.project', { project: id });
  const [editing, setEditing] = useState(false);
  const { busy, run } = useSave();
  const p = data?.project;
  const move = async (s: State) => {
    if (p && (await run(() => action('construction.set_state', { project: p.id, state: s }), t('prj.state_saved')))) void reload();
  };
  return (
    <Shell>
      <div className="page reports prj">
        <nav className="prj-crumbs small">
          <Link to="/projects">{t('prj.title')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{p?.code ?? '…'}</span>
        </nav>
        {!data || !p ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : (
          <>
            <div className="page-head">
              <div>
                <h1>{p.name}</h1>
                <p className="muted prj-meta">
                  <StateBadge s={p.state} />
                  <span>{[p.code, p.customer_name, p.site].filter(Boolean).join(' · ')}</span>
                </p>
              </div>
              {can('construction.manage') ? (
                <div className="row-actions prj-head-actions">
                  {p.state !== 'done' && p.state !== 'cancelled' ? (
                    <Button variant="ghost" icon="edit" onClick={() => setEditing(true)}>
                      {t('prj.edit')}
                    </Button>
                  ) : null}
                  {NEXT[p.state].map((s) => (
                    <Button key={s} variant={s === 'active' && p.state === 'planning' ? 'primary' : 'secondary'} busy={busy} onClick={() => void move(s)}>
                      {t(`prj.to_${s}`)}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="segmented tabs prj-tabs" role="tablist" aria-label={p.name}>
              {TABS.map((x) => (
                <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setParams(x.key === 'overview' ? {} : { tab: x.key }, { replace: true })}>
                  <Icon name={x.icon} size={16} />
                  {t(`prj.tab_${x.key}`)}
                </button>
              ))}
            </div>
            {tab === 'boq' ? (
              <BoqTab d={data} reload={reload} />
            ) : tab === 'certificates' ? (
              <CertTab d={data} reload={reload} />
            ) : tab === 'costs' ? (
              <CostTab d={data} reload={reload} />
            ) : tab === 'diary' ? (
              <DiaryTab d={data} />
            ) : (
              <Overview d={data} reload={reload} />
            )}
          </>
        )}
      </div>
      {editing && p ? (
        <ProjectForm
          project={p}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void reload();
          }}
        />
      ) : null}
    </Shell>
  );
}

function Overview({ d, reload }: { d: Detail; reload: () => unknown }) {
  const f = d.figures;
  const p = d.project;
  const { can } = useSession();
  const { busy, run } = useSave();
  return (
    <div className="rep-grid">
      <section className="rep-kpis" aria-label={t('prj.totals')}>
        <Kpi label={t('prj.k_contract')} value={money(f.contract)} hint={f.variations ? t('prj.k_contract_var', { value: money(f.variations) }) : t('prj.k_contract_hint')} />
        <Kpi label={t('prj.k_certified')} value={money(f.certified)} hint={t('prj.k_certified_hint', { p: pctText(f.progress) })} />
        <Kpi label={t('prj.k_cost')} value={money(f.cost)} warn={f.budget > 0 && f.cost > f.budget} hint={t('prj.k_cost_hint', { budget: money(f.budget), p: pctText(f.spent) })} />
        <Kpi label={t('prj.k_margin')} value={money(f.margin)} warn={f.margin < 0} hint={t('prj.k_margin_hint', { value: money(f.expected_margin) })} />
      </section>
      <section className="panel" aria-labelledby="prj-money">
        <div className="panel-head">
          <h2 id="prj-money">{t('prj.money')}</h2>
        </div>
        <dl className="prj-dl">
          <div>
            <dt>{t('prj.m_certified')}</dt>
            <dd className="tabular">{money(f.certified)}</dd>
          </div>
          <div>
            <dt>{t('prj.m_invoiced')}</dt>
            <dd className="tabular">{money(f.invoiced)}</dd>
          </div>
          <div>
            <dt>{t('prj.m_collected')}</dt>
            <dd className="tabular">{money(f.collected)}</dd>
          </div>
          <div>
            <dt>{t('prj.m_retention', { p: p.retention_pct })}</dt>
            <dd className="tabular">{money(f.retention)}</dd>
          </div>
        </dl>
        {f.retention > 0 ? (
          <div className={`acc-check ${p.retention_released_on ? 'ok' : 'off'}`} role="status">
            <Icon name={p.retention_released_on ? 'checkCircle' : 'lock'} size={16} />
            <span>{p.retention_released_on ? t('prj.ret_released', { date: day(p.retention_released_on), value: money(f.retention) }) : t('prj.ret_held', { value: money(f.retention) })}</span>
            {p.state === 'done' && !p.retention_released_on && can('construction.approve') ? (
              <Button variant="secondary" busy={busy} onClick={async () => (await run(() => action('construction.release_retention', { project: p.id }), t('prj.ret_ok'))) && reload()}>
                {t('prj.ret_release')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>
      <section className="panel" aria-labelledby="prj-costs">
        <div className="panel-head">
          <h2 id="prj-costs">{t('prj.cost_split')}</h2>
        </div>
        <Table
          head={[t('prj.c_kind'), t('prj.c_amount'), t('prj.c_share')]}
          rows={KINDS.map((k) => ({
            key: k,
            cells: [
              <span className="prj-kind">
                <Icon name={KIND_ICON[k]} size={16} />
                {t(`prj.ck_${k}`)}
              </span>,
              <span className="tabular">{money(f.costs[k])}</span>,
              <span className="tabular">{f.cost ? `${Math.round((f.costs[k] / f.cost) * 100)} %` : '—'}</span>,
            ],
          }))}
          foot={[t('prj.total'), <span className="tabular strong">{money(f.cost)}</span>, '']}
        />
      </section>
      <section className="panel rep-wide" aria-labelledby="prj-about">
        <div className="panel-head">
          <h2 id="prj-about">{t('prj.about')}</h2>
        </div>
        <dl className="prj-dl">
          <div>
            <dt>{t('prj.f_manager')}</dt>
            <dd>{p.manager_name ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('prj.f_start')}</dt>
            <dd>{p.start_date ? day(p.start_date) : '—'}</dd>
          </div>
          <div>
            <dt>{t('prj.f_end')}</dt>
            <dd className={p.end_date && p.end_date < d.today && p.state === 'active' ? 'text-danger' : undefined}>{p.end_date ? day(p.end_date) : '—'}</dd>
          </div>
          <div>
            <dt>{t('prj.f_store')}</dt>
            <dd>{p.location_name ?? t('prj.main_store')}</dd>
          </div>
        </dl>
        {p.note ? <p className="muted small">{p.note}</p> : null}
      </section>
      <CustomFieldsView target="construction.project" record={p.id} version={d} className="rep-wide" />
    </div>
  );
}

/* ---------------------------------------------------------------- Bill of quantities */

function BoqTab({ d, reload }: { d: Detail; reload: () => unknown }) {
  const { can } = useSession();
  const [editing, setEditing] = useState(false);
  const closed = d.project.state === 'done' || d.project.state === 'cancelled';
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <span className="muted small">{t('prj.boq_hint')}</span>
        {can('construction.manage') && !closed ? (
          <Button variant="primary" icon="edit" onClick={() => setEditing(true)}>
            {d.items.length ? t('prj.boq_edit') : t('prj.boq_add')}
          </Button>
        ) : null}
      </div>
      <section className="panel rep-wide" aria-label={t('prj.tab_boq')}>
        {!d.items.length ? (
          <EmptyState icon="list" title={t('prj.boq_empty')} body={t('prj.boq_empty_body')} />
        ) : (
          <Table
            head={[t('prj.c_line'), t('prj.c_qty'), t('prj.c_price'), t('prj.c_value'), t('prj.c_done'), t('prj.c_cost')]}
            phoneHide={[1, 2, 5]}
            rows={d.items.map((i) => ({
              key: i.id,
              cells: [
                <span className="prj-name">
                  <span className="strong">{[i.code, i.name].filter(Boolean).join(' ')}</span>
                  {i.variation ? <span className="muted small">{t('prj.variation')}</span> : null}
                </span>,
                <span className="tabular">
                  {qtyText(i.qty)} {unit(i.unit)}
                </span>,
                <span className="tabular">{money(i.unit_price)}</span>,
                <span className="tabular strong">{money(i.value)}</span>,
                <span className="prj-prog">
                  <Bar pct={i.progress} label={i.name} />
                  <span className="tabular small">{i.progress} %</span>
                </span>,
                <span className={`tabular${i.over_budget ? ' text-danger' : ''}`} title={t('prj.of_budget', { value: money(i.budget) })}>
                  {money(i.cost)}
                </span>,
              ],
            }))}
            foot={[t('prj.total'), '', '', <span className="tabular strong">{money(d.figures.contract)}</span>, pctText(d.figures.progress), <span className="tabular">{money(d.figures.cost)}</span>]}
          />
        )}
      </section>
      {editing ? (
        <BoqEditor
          d={d}
          onClose={() => setEditing(false)}
          onDone={() => {
            setEditing(false);
            void reload();
          }}
        />
      ) : null}
    </div>
  );
}

interface BoqRow {
  key: string;
  id?: string;
  code: string;
  name: string;
  unit: Unit;
  qty: string;
  price: string;
  cost: string;
  certified: number;
}

function BoqEditor({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: () => void }) {
  const [rows, setRows] = useState<BoqRow[]>(() =>
    d.items.length
      ? d.items.map((i) => ({ key: i.id, id: i.id, code: i.code ?? '', name: i.name, unit: i.unit, qty: String(i.qty), price: lek(i.unit_price), cost: lek(i.budget_unit_cost), certified: i.certified }))
      : [{ key: 'n0', code: '1', name: '', unit: 'm3', qty: '', price: '', cost: '', certified: 0 }],
  );
  const [touched, setTouched] = useState(false);
  const { busy, run } = useSave();
  const parsed = rows.map((r) => ({ qty: parseQty(r.qty), price: parseMoney(r.price || '0'), cost: parseMoney(r.cost || '0') }));
  const bad = (i: number) => !rows[i]!.name.trim() || !parsed[i]!.qty || parsed[i]!.price === null || parsed[i]!.cost === null;
  const ok = rows.every((_, i) => !bad(i));
  const edit = (i: number, patch: Partial<BoqRow>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const total = parsed.reduce((s, x) => s + (x.qty && x.price ? Math.round(x.qty * x.price) : 0), 0);
  const save = async () => {
    setTouched(true);
    if (!ok) return;
    const items = rows.map((r, i) => ({ id: r.id, code: r.code.trim() || null, name: r.name.trim(), unit: r.unit, qty: parsed[i]!.qty!, unit_price: parsed[i]!.price!, budget_unit_cost: parsed[i]!.cost! }));
    if (await run(() => action('construction.save_items', { project: d.project.id, items }), t('prj.boq_saved'))) onDone();
  };
  return (
    <Dialog
      title={t('prj.tab_boq')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="foot-actions">
            <Button variant="ghost" icon="plus" onClick={() => setRows([...rows, { key: `n${Date.now()}`, code: '', name: '', unit: rows.at(-1)?.unit ?? 'm3', qty: '', price: '', cost: '', certified: 0 }])}>
              {t('prj.add_line')}
            </Button>
            <span className="muted small tabular">{t('prj.boq_total', { value: money(total) })}</span>
          </span>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack prj-boq">
        {d.project.state !== 'planning' ? <p className="acc-check off small">{t('prj.boq_variation_note')}</p> : null}
        <div className="prj-boq-head" aria-hidden="true">
          <span>{t('prj.c_code')}</span>
          <span>{t('prj.c_line')}</span>
          <span>{t('prj.c_unit')}</span>
          <span>{t('prj.c_qty')}</span>
          <span>{t('prj.c_price')}</span>
          <span>{t('prj.c_budget_cost')}</span>
          <span />
        </div>
        <ol className="prj-boq-rows">
          {rows.map((r, i) => (
            <li key={r.key} className={touched && bad(i) ? 'invalid' : undefined}>
              <input className="input" aria-label={t('prj.c_code')} value={r.code} maxLength={20} onChange={(e) => edit(i, { code: e.target.value })} />
              <input className={`input${touched && !r.name.trim() ? ' invalid' : ''}`} aria-label={t('prj.c_line')} value={r.name} maxLength={200} placeholder={t('prj.line_ph')} onChange={(e) => edit(i, { name: e.target.value })} />
              <select className="input" aria-label={t('prj.c_unit')} value={r.unit} disabled={r.certified > 0} onChange={(e) => edit(i, { unit: e.target.value as Unit })}>
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {unit(u)}
                  </option>
                ))}
              </select>
              <input className={`input tabular${touched && !parsed[i]!.qty ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('prj.c_qty')} value={r.qty} onChange={(e) => edit(i, { qty: e.target.value })} />
              <input className={`input tabular${touched && parsed[i]!.price === null ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('prj.c_price')} value={r.price} disabled={r.certified > 0} placeholder="0" onChange={(e) => edit(i, { price: e.target.value })} />
              <input className={`input tabular${touched && parsed[i]!.cost === null ? ' invalid' : ''}`} inputMode="decimal" aria-label={t('prj.c_budget_cost')} value={r.cost} placeholder="0" onChange={(e) => edit(i, { cost: e.target.value })} />
              <span className="row-actions">
                <IconButton icon="trash" label={t('prj.remove_line')} disabled={r.certified > 0 || rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))} />
              </span>
            </li>
          ))}
        </ol>
        <p className="muted small">{t('prj.boq_prices_hint')}</p>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Certificates */

function CertTab({ d, reload }: { d: Detail; reload: () => unknown }) {
  const { can } = useSession();
  const [open, setOpen] = useState<string | null>(null);
  const { busy, run } = useSave();
  const draft = d.certificates.find((c) => c.state === 'draft');
  const start = async () => {
    let id = '';
    if (await run(async () => void (id = (await action<{ id: string }>('construction.new_certificate', { project: d.project.id })).id))) {
      void reload();
      setOpen(id);
    }
  };
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <span className="muted small">{t('prj.cert_hint')}</span>
        {can('construction.manage') && d.project.state === 'active' ? (
          draft ? (
            <Button variant="primary" icon="edit" onClick={() => setOpen(draft.id)}>
              {t('prj.cert_continue', { n: draft.number })}
            </Button>
          ) : (
            <Button variant="primary" icon="plus" busy={busy} disabled={!d.items.length} onClick={start}>
              {t('prj.cert_new')}
            </Button>
          )
        ) : null}
      </div>
      <section className="panel rep-wide" aria-label={t('prj.tab_certificates')}>
        {!d.certificates.length ? (
          <EmptyState icon="invoice" title={t('prj.cert_empty')} body={t('prj.cert_empty_body')} />
        ) : (
          <Table
            head={[t('prj.c_cert'), t('prj.c_period'), t('prj.c_net'), t('prj.c_retention'), t('prj.c_invoice'), t('prj.c_state')]}
            left={[1, 4]}
            phoneHide={[1, 2, 3, 4]}
            rows={d.certificates.map((c) => ({
              key: c.id,
              onClick: () => setOpen(c.id),
              cells: [
                <span className="strong">{t('prj.cert_n', { n: c.number })}</span>,
                <span className="small">{c.period_from ? `${day(c.period_from)} – ${day(c.period_to)}` : day(c.period_to)}</span>,
                <span className="tabular strong">{money(c.net)}</span>,
                <span className="tabular">{c.retention ? money(c.retention) : '—'}</span>,
                c.invoice_name ? (
                  <span className="small">
                    {c.invoice_name}
                    {c.invoice_open ? ` · ${t('prj.open_amount', { value: money(c.invoice_open) })}` : ''}
                  </span>
                ) : c.invoice ? (
                  <span className="small muted">{c.invoice_state === 'cancelled' ? t('prj.inv_cancelled') : t('prj.inv_draft')}</span>
                ) : (
                  '—'
                ),
                <CertBadge s={c.state} />,
              ],
            }))}
          />
        )}
      </section>
      {open ? (
        <CertDialog
          id={open}
          onClose={() => setOpen(null)}
          onChanged={() => void reload()}
        />
      ) : null}
    </div>
  );
}

interface CertDetail {
  certificate: { id: string; number: number; period_from: string | null; period_to: string; state: CertState; net: number; tax: number; total: number; retention: number; invoice: string | null; invoice_name: string | null; invoice_state: string | null; approved_by_name: string | null; approved_at: string | null; note: string | null };
  project: { id: string; code: string; name: string; customer_name: string; tax_rate: number; retention_pct: number };
  lines: { item: string; code: string | null; name: string; unit: Unit; contract_qty: number; unit_price: number; variation: boolean; before: number; qty: number; to_date: number; amount: number; to_date_amount: number }[];
  totals: { contract: number; before: number; to_date: number };
}

function CertDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const { data, reload } = useAction<CertDetail>('construction.certificate', { certificate: id });
  const [qty, setQty] = useState<Record<string, string> | null>(null);
  const [period, setPeriod] = useState<{ from: string; to: string; note: string } | null>(null);
  const { busy, run } = useSave();
  if (!data) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose} size="xl">
        <Skeleton h={300} />
      </Dialog>
    );
  }
  const c = data.certificate;
  const draft = c.state === 'draft';
  const values = qty ?? Object.fromEntries(data.lines.map((l) => [l.item, l.qty ? String(l.qty) : '']));
  const per = period ?? { from: c.period_from ?? '', to: c.period_to, note: c.note ?? '' };
  const parsed = data.lines.map((l) => {
    const raw = (values[l.item] ?? '').trim();
    const q = raw === '' ? 0 : parseQty(raw);
    return { line: l, q, over: q !== null && l.before + q > l.contract_qty + 1e-9 };
  });
  const bad = parsed.some((x) => x.q === null || x.over);
  const net = parsed.reduce((s, x) => s + (x.q ? Math.round(x.q * x.line.unit_price) : 0), 0);
  const changed = () => {
    void reload();
    onChanged();
  };
  const save = async (then?: () => Promise<unknown>) => {
    const ok = await run(
      () =>
        action('construction.save_certificate', {
          certificate: c.id,
          period_from: per.from || null,
          period_to: per.to,
          note: per.note,
          lines: parsed.filter((x) => x.q).map((x) => ({ item: x.line.item, qty: x.q })),
        }),
      then ? undefined : t('prj.cert_saved'),
    );
    if (ok && then) await run(then, t('prj.cert_approved'));
    setQty(null);
    setPeriod(null);
    changed();
  };
  const act = async (name: string, msg: string) => (await run(() => action(name, { certificate: c.id }), msg)) && changed();
  return (
    <Dialog
      title={t('prj.cert_title', { n: c.number, code: data.project.code })}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="foot-actions">
            {can('construction.approve') && (c.state === 'draft' || c.state === 'approved' || (c.state === 'invoiced' && c.invoice_state === 'cancelled')) ? (
              <Button variant="ghost" icon="x" busy={busy} onClick={() => act('construction.cancel_certificate', t('prj.cert_cancelled'))}>
                {t('prj.cert_cancel')}
              </Button>
            ) : null}
          </span>
          {draft && can('construction.manage') ? (
            <span className="row-actions">
              <Button variant="secondary" icon="check" busy={busy} disabled={bad || !per.to} onClick={() => save()}>
                {t('common.save')}
              </Button>
              {can('construction.approve') ? (
                <Button variant="primary" icon="checkCircle" busy={busy} disabled={bad || !per.to || net <= 0} onClick={() => save(() => action('construction.approve_certificate', { certificate: c.id }))}>
                  {t('prj.cert_approve')}
                </Button>
              ) : null}
            </span>
          ) : (c.state === 'approved' || (c.state === 'invoiced' && c.invoice_state === 'cancelled')) && can('construction.approve') ? (
            <Button variant="primary" icon="invoice" busy={busy} onClick={() => act('construction.invoice_certificate', t('prj.cert_invoiced'))}>
              {t('prj.cert_invoice')}
            </Button>
          ) : c.invoice && c.invoice_state !== 'cancelled' ? (
            <Button variant="secondary" icon="invoice" onClick={() => navigate(`/invoices?open=${c.invoice}`)}>
              {t('prj.open_invoice', { name: c.invoice_name ?? t('prj.inv_draft') })}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack prj-cert">
        <div className="prj-cert-top">
          <CertBadge s={c.state} />
          <span className="muted small">{[data.project.name, data.project.customer_name, c.approved_by_name ? t('prj.approved_by', { name: c.approved_by_name }) : null].filter(Boolean).join(' · ')}</span>
        </div>
        {draft ? (
          <div className="dn-fields">
            <Field label={t('prj.from')}>
              <input className="input" type="date" value={per.from} onChange={(e) => setPeriod({ ...per, from: e.target.value })} />
            </Field>
            <Field label={t('prj.to')} required>
              <input className="input" type="date" value={per.to} onChange={(e) => setPeriod({ ...per, to: e.target.value })} />
            </Field>
          </div>
        ) : (
          <p className="small">{t('prj.period', { from: c.period_from ? day(c.period_from) : '—', to: day(c.period_to) })}</p>
        )}
        <div className="prj-table-wrap">
          <table className="data-table prj-cert-table">
            <thead>
              <tr>
                <th scope="col">{t('prj.c_line')}</th>
                <th scope="col" className="hide-phone">{t('prj.c_contract_qty')}</th>
                <th scope="col" className="hide-phone">{t('prj.c_before')}</th>
                <th scope="col">{t('prj.c_this')}</th>
                <th scope="col" className="hide-phone">{t('prj.c_to_date')}</th>
                <th scope="col">{t('prj.c_amount')}</th>
              </tr>
            </thead>
            <tbody>
              {parsed.map(({ line: l, q, over }) => (
                <tr key={l.item} className={over ? 'row-bad' : undefined}>
                  <td>
                    <span className="prj-name">
                      <span className="strong">{[l.code, l.name].filter(Boolean).join(' ')}</span>
                      <span className="muted small">
                        {money(l.unit_price)} / {unit(l.unit)}
                        {over ? ` · ${t('prj.over_contract')}` : ''}
                      </span>
                    </span>
                  </td>
                  <td className="hide-phone tabular">{qtyText(l.contract_qty)}</td>
                  <td className="hide-phone tabular">{qtyText(l.before)}</td>
                  <td className="tabular">
                    {draft ? (
                      <input
                        className={`input tabular prj-qty${q === null || over ? ' invalid' : ''}`}
                        inputMode="decimal"
                        aria-label={t('prj.qty_for', { name: l.name })}
                        placeholder="0"
                        value={values[l.item] ?? ''}
                        onChange={(e) => setQty({ ...values, [l.item]: e.target.value })}
                      />
                    ) : (
                      qtyText(l.qty)
                    )}
                  </td>
                  <td className="hide-phone tabular">{qtyText(l.before + (q ?? 0))}</td>
                  <td className="tabular strong">{q ? money(Math.round(q * l.unit_price)) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <dl className="prj-dl prj-cert-sum">
          <div>
            <dt>{t('prj.s_net')}</dt>
            <dd className="tabular strong">{money(draft ? net : c.net)}</dd>
          </div>
          <div>
            <dt>{t('prj.s_vat', { p: data.project.tax_rate })}</dt>
            <dd className="tabular">{money(draft ? Math.round((net * data.project.tax_rate) / 100) : c.tax)}</dd>
          </div>
          <div>
            <dt>{t('prj.s_total')}</dt>
            <dd className="tabular strong">{money(draft ? net + Math.round((net * data.project.tax_rate) / 100) : c.total)}</dd>
          </div>
          {data.project.retention_pct ? (
            <div>
              <dt>{t('prj.s_retention', { p: data.project.retention_pct })}</dt>
              <dd className="tabular">{money(draft ? Math.round((net * data.project.retention_pct) / 100) : c.retention)}</dd>
            </div>
          ) : null}
          <div>
            <dt>{t('prj.s_to_date')}</dt>
            <dd className="tabular">{t('prj.s_to_date_v', { done: money(data.totals.before + (draft ? net : c.net)), of: money(data.totals.contract) })}</dd>
          </div>
        </dl>
        {draft ? (
          <Field label={t('prj.f_note')}>
            <textarea className="input" rows={2} maxLength={1000} value={per.note} onChange={(e) => setPeriod({ ...per, note: e.target.value })} />
          </Field>
        ) : c.note ? (
          <p className="muted small">{c.note}</p>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Costs */

function CostTab({ d, reload }: { d: Detail; reload: () => unknown }) {
  const { can } = useSession();
  const [dlg, setDlg] = useState<'material' | 'cost' | 'bill' | null>(null);
  const { busy, run } = useSave();
  const closed = d.project.state === 'done' || d.project.state === 'cancelled';
  const done = () => {
    setDlg(null);
    void reload();
  };
  const itemName = (id: string | null) => {
    const i = id ? d.items.find((x) => x.id === id) : null;
    return i ? [i.code, i.name].filter(Boolean).join(' ') : null;
  };
  return (
    <div className="rep-grid">
      {!closed ? (
        <div className="toolbar">
          <div className="toolbar-group">
            {can('construction.site') && d.project.state !== 'planning' ? (
              <Button variant="primary" icon="box" onClick={() => setDlg('material')}>
                {t('prj.take_material')}
              </Button>
            ) : null}
            {can('construction.manage') ? (
              <Button variant="secondary" icon="plus" onClick={() => setDlg('cost')}>
                {t('prj.add_cost')}
              </Button>
            ) : null}
            {can('construction.manage') && d.has_purchase ? (
              <Button variant="secondary" icon="link" onClick={() => setDlg('bill')}>
                {t('prj.book_bill')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <section className="panel rep-wide" aria-label={t('prj.tab_costs')}>
        {!d.costs.length ? (
          <EmptyState icon="cash" title={t('prj.cost_empty')} body={t('prj.cost_empty_body')} />
        ) : (
          <Table
            head={[t('prj.c_date'), t('prj.c_what'), t('prj.c_kind'), t('prj.c_amount'), '']}
            left={[1, 2]}
            phoneHide={[0, 2]}
            rows={d.costs.map((k) => ({
              key: k.id,
              cells: [
                <span className="small">{day(k.date)}</span>,
                <span className="prj-name">
                  <span>{k.description}</span>
                  {itemName(k.item) ? <span className="muted small">{itemName(k.item)}</span> : null}
                </span>,
                <span className="prj-kind small">
                  <Icon name={KIND_ICON[k.kind]} size={14} />
                  {t(`prj.ck_${k.kind}`)}
                </span>,
                <span className={`tabular strong${k.amount < 0 ? ' text-ok' : ''}`}>{money(k.amount)}</span>,
                !k.source && can('construction.manage') && !closed ? (
                  <IconButton icon="trash" label={t('prj.remove_cost')} disabled={busy} onClick={async () => (await run(() => action('construction.remove_cost', { cost: k.id }), t('prj.cost_removed'))) && reload()} />
                ) : (
                  ''
                ),
              ],
            }))}
            foot={['', t('prj.total'), '', <span className="tabular strong">{money(d.figures.cost)}</span>, '']}
          />
        )}
      </section>
      {dlg === 'material' ? <MaterialDialog d={d} onClose={() => setDlg(null)} onDone={done} /> : null}
      {dlg === 'cost' ? <CostDialog d={d} onClose={() => setDlg(null)} onDone={done} /> : null}
      {dlg === 'bill' ? <BillDialog d={d} onClose={() => setDlg(null)} onDone={done} /> : null}
    </div>
  );
}

function ItemSelect({ d, value, onChange }: { d: Detail; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={t('prj.f_line')} hint={t('prj.f_line_hint')}>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('prj.whole_project')}</option>
        {d.items.map((i) => (
          <option key={i.id} value={i.id}>
            {[i.code, i.name].filter(Boolean).join(' ')}
          </option>
        ))}
      </select>
    </Field>
  );
}

function MaterialDialog({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: () => void }) {
  const { data } = useAction<{ id: string; name: string; unit: Unit; unit_cost: number; on_hand: number; on_project: number }[]>('construction.materials', { project: d.project.id });
  const [stock, setStock] = useState('');
  const [back, setBack] = useState(false);
  const [q, setQ] = useState('');
  const [item, setItem] = useState('');
  const [note, setNote] = useState('');
  const { busy, run } = useSave();
  const m = data?.find((x) => x.id === stock);
  const n = parseQty(q);
  const max = m ? (back ? m.on_project : m.on_hand) : 0;
  const ok = !!m && !!n && n <= max;
  const save = async () => {
    if (ok && (await run(() => action('construction.issue_material', { project: d.project.id, stock_item: stock, qty: back ? -n! : n, item: item || null, note }), back ? t('prj.mat_back_ok') : t('prj.mat_ok')))) onDone();
  };
  return (
    <Dialog
      title={t('prj.take_material')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {back ? t('prj.mat_back') : t('prj.mat_take')}
          </Button>
        </>
      }
    >
      {!data ? (
        <Skeleton h={200} />
      ) : !data.length ? (
        <EmptyState icon="box" title={t('prj.mat_none')} body={t('prj.mat_none_body')} />
      ) : (
        <div className="form-stack">
          <div className="segmented" role="radiogroup" aria-label={t('prj.mat_way')}>
            {[false, true].map((b) => (
              <button key={String(b)} type="button" role="radio" aria-checked={back === b} className={back === b ? 'active' : ''} onClick={() => setBack(b)}>
                {b ? t('prj.mat_way_back') : t('prj.mat_way_to')}
              </button>
            ))}
          </div>
          <Field label={t('prj.f_material')} required>
            <select className="input" value={stock} onChange={(e) => setStock(e.target.value)} data-autofocus>
              <option value="">{t('prj.choose')}</option>
              {data
                .filter((x) => (back ? x.on_project > 0 : true))
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} · {back ? t('prj.on_site', { n: qtyText(x.on_project) }) : t('prj.in_store', { n: qtyText(x.on_hand) })}
                  </option>
                ))}
            </select>
          </Field>
          <Field label={m ? t('prj.f_qty_unit', { unit: t(`stock.unit_${m.unit}` as MessageKey) }) : t('prj.c_qty')} required error={m && n && n > max ? t('prj.mat_max', { n: qtyText(max) }) : q && !n ? t('prj.qty_bad') : null}>
            <input className="input tabular" inputMode="decimal" value={q} onChange={(e) => setQ(e.target.value)} />
          </Field>
          {m && n ? <p className="muted small">{t('prj.mat_value', { value: money(Math.round(n * m.unit_cost)) })}</p> : null}
          <ItemSelect d={d} value={item} onChange={setItem} />
          <Field label={t('prj.f_note')}>
            <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

function CostDialog({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: () => void }) {
  const [kind, setKind] = useState<'labour' | 'subcontract' | 'equipment' | 'other'>('labour');
  const [date, setDate] = useState(todayIso());
  const [what, setWhat] = useState('');
  const [byRate, setByRate] = useState(true);
  const [q, setQ] = useState('');
  const [rate, setRate] = useState('');
  const [amount, setAmount] = useState('');
  const [item, setItem] = useState('');
  const { busy, run } = useSave();
  const n = parseQty(q);
  const r = parseMoney(rate);
  const a = parseMoney(amount);
  const value = byRate ? (n && r !== null ? Math.round(n * r) : null) : a;
  const ok = what.trim().length >= 2 && date && !!value && value > 0;
  const save = async () => {
    if (!ok) return;
    const input = { project: d.project.id, kind, date, description: what.trim(), item: item || null, ...(byRate ? { qty: n, rate: r } : { amount: a }) };
    if (await run(() => action('construction.add_cost', input), t('prj.cost_ok'))) onDone();
  };
  return (
    <Dialog
      title={t('prj.add_cost')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="segmented prj-kinds" role="radiogroup" aria-label={t('prj.c_kind')}>
          {(['labour', 'subcontract', 'equipment', 'other'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              <Icon name={KIND_ICON[k]} size={16} />
              {t(`prj.ck_${k}`)}
            </button>
          ))}
        </div>
        <Field label={t('prj.c_what')} required>
          <input className="input" value={what} maxLength={200} placeholder={t(`prj.ph_${kind}`)} onChange={(e) => setWhat(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('prj.c_date')} required>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <div className="segmented" role="radiogroup" aria-label={t('prj.how_much')}>
          {[true, false].map((b) => (
            <button key={String(b)} type="button" role="radio" aria-checked={byRate === b} className={byRate === b ? 'active' : ''} onClick={() => setByRate(b)}>
              {b ? t('prj.by_rate') : t('prj.by_amount')}
            </button>
          ))}
        </div>
        {byRate ? (
          <div className="dn-fields">
            <Field label={t('prj.f_hours')} hint={t('prj.f_hours_hint')}>
              <input className="input tabular" inputMode="decimal" value={q} onChange={(e) => setQ(e.target.value)} />
            </Field>
            <Field label={t('prj.f_rate')}>
              <input className="input tabular" inputMode="decimal" value={rate} placeholder="0" onChange={(e) => setRate(e.target.value)} />
            </Field>
          </div>
        ) : (
          <Field label={t('prj.f_amount')} hint={t('prj.f_amount_hint')}>
            <input className="input tabular" inputMode="decimal" value={amount} placeholder="0" onChange={(e) => setAmount(e.target.value)} />
          </Field>
        )}
        {value ? <p className="strong tabular">{t('prj.cost_total', { value: money(value) })}</p> : null}
        <ItemSelect d={d} value={item} onChange={setItem} />
        <p className="muted small">{t('prj.cost_books_note')}</p>
      </div>
    </Dialog>
  );
}

function BillDialog({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: () => void }) {
  const [search, setSearch] = useState('');
  const { data } = useAction<{ id: string; name: string; supplier_ref: string | null; date: string; supplier_name: string; net: number }[]>('construction.bills', { search: search.trim() || undefined });
  const [kind, setKind] = useState<'material' | 'subcontract' | 'equipment' | 'other'>('subcontract');
  const [item, setItem] = useState('');
  const { busy, run } = useSave();
  return (
    <Dialog title={t('prj.book_bill')} onClose={onClose}>
      <div className="form-stack">
        <div className="segmented prj-kinds" role="radiogroup" aria-label={t('prj.c_kind')}>
          {(['subcontract', 'material', 'equipment', 'other'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              <Icon name={KIND_ICON[k]} size={16} />
              {t(`prj.ck_${k}`)}
            </button>
          ))}
        </div>
        <ItemSelect d={d} value={item} onChange={setItem} />
        <input className="input" type="search" placeholder={t('prj.bill_search')} aria-label={t('prj.bill_search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        {!data ? (
          <Skeleton h={120} />
        ) : !data.length ? (
          <EmptyState icon="receipt" title={t('prj.bill_none')} body={t('prj.bill_none_body')} />
        ) : (
          <ul className="dst-pick">
            {data.map((b) => (
              <li key={b.id}>
                <button type="button" disabled={busy} onClick={async () => (await run(() => action('construction.link_bill', { project: d.project.id, bill: b.id, kind, item: item || null }), t('prj.bill_ok'))) && onDone()}>
                  <span className="prj-name">
                    <span className="strong">{b.supplier_name}</span>
                    <span className="muted small">{[b.name, b.supplier_ref, day(b.date)].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="tabular strong">{money(b.net)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="muted small">{t('prj.bill_net_note')}</p>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Site diary */

interface Page {
  id: string;
  date: string;
  weather: string | null;
  workers: number;
  work_done: string;
  problems: string | null;
  written_by_name: string | null;
}

function DiaryTab({ d }: { d: Detail }) {
  const { can } = useSession();
  const [v, setV] = useState(0);
  const { data } = useAction<Page[]>('construction.diary', { project: d.project.id, v });
  const [writing, setWriting] = useState<Page | 'new' | null>(null);
  const writable = can('construction.site') && (d.project.state === 'active' || d.project.state === 'on_hold');
  const todayPage = data?.find((p) => p.date === d.today);
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <span className="muted small">{t('prj.diary_hint')}</span>
        {writable ? (
          <Button variant="primary" icon={todayPage ? 'edit' : 'plus'} onClick={() => setWriting(todayPage ?? 'new')}>
            {todayPage ? t('prj.diary_edit_today') : t('prj.diary_write')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : !data.length ? (
        <div className="rep-wide">
          <EmptyState icon="book" title={t('prj.diary_empty')} body={t('prj.diary_empty_body')} />
        </div>
      ) : (
        <ol className="prj-diary rep-wide">
          {data.map((p) => (
            <li key={p.id} className="panel">
              <div className="prj-diary-head">
                <span className="strong">{day(p.date)}</span>
                <span className="muted small">{[p.weather, t('prj.workers_n', { n: p.workers }), p.written_by_name].filter(Boolean).join(' · ')}</span>
                {writable ? <IconButton icon="edit" label={t('prj.diary_edit')} onClick={() => setWriting(p)} /> : null}
              </div>
              <p>{p.work_done}</p>
              {p.problems ? (
                <p className="prj-problem small">
                  <Icon name="alert" size={14} />
                  {p.problems}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {writing ? (
        <DiaryForm
          project={d.project.id}
          page={writing === 'new' ? null : writing}
          today={d.today}
          onClose={() => setWriting(null)}
          onDone={() => {
            setWriting(null);
            setV((x) => x + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function DiaryForm({ project, page, today, onClose, onDone }: { project: string; page: Page | null; today: string; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState(page?.date ?? today);
  const [weather, setWeather] = useState(page?.weather ?? '');
  const [workers, setWorkers] = useState(page ? String(page.workers) : '');
  const [work, setWork] = useState(page?.work_done ?? '');
  const [problems, setProblems] = useState(page?.problems ?? '');
  const { busy, run } = useSave();
  const w = workers.trim() === '' ? 0 : Number(workers);
  const ok = work.trim().length >= 3 && date && date <= today && Number.isInteger(w) && w >= 0;
  const save = async () => {
    if (ok && (await run(() => action('construction.save_diary', { project, date, weather, workers: w, work_done: work.trim(), problems }), t('prj.diary_saved')))) onDone();
  };
  return (
    <Dialog
      title={t('prj.diary_page')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="dn-fields">
          <Field label={t('prj.c_date')} required error={date > today ? t('prj.diary_future') : null}>
            <input className="input" type="date" value={date} max={today} disabled={!!page} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label={t('prj.f_workers')}>
            <input className="input tabular" inputMode="numeric" value={workers} onChange={(e) => setWorkers(e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>
        <Field label={t('prj.f_weather')}>
          <input className="input" value={weather} maxLength={60} placeholder={t('prj.weather_ph')} onChange={(e) => setWeather(e.target.value)} />
        </Field>
        <Field label={t('prj.f_work')} required>
          <textarea className="input" rows={4} maxLength={4000} value={work} onChange={(e) => setWork(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('prj.f_problems')} hint={t('prj.f_problems_hint')}>
          <textarea className="input" rows={3} maxLength={4000} value={problems} onChange={(e) => setProblems(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
